import { FC } from 'react';
import { GoDotFill } from '@react-icons/all-files/go/GoDotFill';
import { MdCheck } from '@react-icons/all-files/md/MdCheck';
import { MdOutlineKeyboardArrowRight } from '@react-icons/all-files/md/MdOutlineKeyboardArrowRight';
import classNames from 'classnames';

import { Pill } from 'components/design-system/Pill';
import { useHandleRequest } from 'components/purchaseRequests/useHandleRequest';
import { useModal } from 'context/modal/ModalContext';
import styles from 'styles/PurchaseRequest.module.scss';
import { PurchaseRequest, PurchaseRequestOffer } from 'types';
import { formatPrice } from 'utils/text-helpers';

import { RequestsCountdown } from '../components/RequestsCountdown';
import { RequestConversationSlideout } from '../RequestConversationSlideout';

type OpenRequestItemProps = {
  isYourRequest: boolean;
  purchaseRequest: PurchaseRequest;
  requestOffers: PurchaseRequestOffer[];
};

export const OpenRequestItem: FC<OpenRequestItemProps> = ({ purchaseRequest, requestOffers, isYourRequest }) => {
  const { closeModal, openModal } = useModal();
  const { onRespond } = useHandleRequest(purchaseRequest, requestOffers);

  const onYourRequestClick = () =>
    openModal(<RequestConversationSlideout canGoBack request={purchaseRequest} closeModal={closeModal} />, true);

  return (
    <div
      onClick={isYourRequest ? onYourRequestClick : onRespond}
      role='button'
      className={classNames(
        'my-2 border border-primary12 ps-3 pe-2 py-3 justify-content-between align-items-center riforma-regular w-100 rounded-2 d-flex position-relative',
        styles.selectionItem,
        { 'border-success bg-success06': isYourRequest }
      )}
    >
      <div className='w-65'>
        <div className={classNames('fs-16px riforma-medium opacity-90', { 'text-success': isYourRequest })}>
          {purchaseRequest.yearRestriction ? `${purchaseRequest.yearRestriction} or newer` : 'Any year'}
        </div>
        <div className='fs-12px d-flex'>
          {!isYourRequest && (
            <span className='d-flex letter-spacing-08 align-items-center text-success pe-1'>
              <MdCheck />
              <span className='ps-1'>DEPOSIT</span>
            </span>
          )}
          {isYourRequest && <span className='letter-spacing-08 text-success'>YOUR REQUEST</span>}
          {purchaseRequest.expiration && (
            <span className='opacity-60 d-flex align-items-center'>
              <GoDotFill />
              <RequestsCountdown textType='left' expiration={purchaseRequest.expiration} />
            </span>
          )}
        </div>
      </div>
      <div className='d-flex align-items-center justify-content-end'>
        <div className='fs-24px text-success'>{formatPrice(purchaseRequest.requestedPriceCents)}</div>
        <div className='opacity-40 ms-1 d-flex align-items-center justify-content-center'>
          <MdOutlineKeyboardArrowRight size={25} />
        </div>
      </div>
      {requestOffers.length > 0 && (
        <div style={{ right: 25, height: 17, top: -7 }} className='position-absolute'>
          <Pill title='YOU OFFERED' className='py-1 px-3 fs-10px border-radius-46 text-white' color='success' />
        </div>
      )}
    </div>
  );
};
